/**
 * Paramétrage des examens nationaux, onglet « Candidats et cycle »
 * (GO de Daniel du 01/11/2026).
 *
 *   Dernier examen de l'école   ses admis partent par nature : ils sortent du
 *                               taux de réinscription réel et sont marqués
 *                               « fin de cycle ». BAC par défaut. Régime A :
 *                               le choix s'enregistre aussitôt.
 *   Classes présentées          pour chaque examen et pour l'année affichée,
 *                               les classes dont les élèves sont candidats.
 *                               Sans réglage, KLASYS les devine par le nom
 *                               (CM2, 3e sauf P3, Tle) ; une classe nommée
 *                               autrement disparaissait alors en silence.
 *                               Régime B : brouillon, puis Enregistrer.
 *
 * Norme : PARAMETRAGE-MODULES.md (section, cartes, lignes, interrupteurs,
 * régimes d'enregistrement, chargement et échec).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, RotateCcw, Save, Info, AlertTriangle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import CarteReglages from '../parametrage/CarteReglages';
import ChargementSection from '../parametrage/ChargementSection';
import PorteurNotification from '../ui/PorteurNotification';
import { useNotification } from '../../hooks/useNotification';
import { EXAMENS_NATIONAUX } from '../../config/examensConfig';
import { api } from '../../services/api';

const ACCENT = '#319190';
const ACCENT_SOMBRE = '#246f6e';
const CODES = ['CEPE', 'BEPC', 'BAC'];

const DESCRIPTIONS_FINAL = {
  CEPE: "L'école s'arrête au primaire : les admis au CEPE partent en 6ème ailleurs.",
  BEPC: "L'école s'arrête au collège : les admis au BEPC partent en 2nde ailleurs.",
  BAC: "L'école va jusqu'au lycée : les admis au BAC quittent l'école, cycle terminé."
};

/** Le brouillon d'un examen : la règle du nom, ou une liste réglée. */
const etatInitial = (examen) => ({
  mode: examen.mode,
  ids: new Set(examen.classes.filter((c) => c.retenue).map((c) => c.id))
});

const memesIds = (a, b) => a.size === b.size && [...a].every((id) => b.has(id));

const Interrupteur = ({ actif, onBasculer, desactive, libelle }) => (
  <button
    type="button"
    onClick={onBasculer}
    disabled={desactive}
    className={`relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 ${desactive ? 'opacity-40' : ''}`}
    style={{ backgroundColor: actif ? ACCENT : '#d1d5db' }}
    aria-pressed={actif}
    aria-label={libelle}
  >
    <div className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform duration-300 ${actif ? 'translate-x-[22px]' : 'translate-x-[3px]'}`} />
  </button>
);

const ParametrageCandidatsExamens = () => {
  const { notification, notifier, masquer } = useNotification();
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState(null);
  const [donnees, setDonnees] = useState(null);
  const [brouillon, setBrouillon] = useState({});
  const [toutesVisibles, setToutesVisibles] = useState({});
  const [examenFinal, setExamenFinal] = useState('BAC');
  const [enregistrementFinal, setEnregistrementFinal] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [reprise, setReprise] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setEchec(null);
    try {
      const reponse = await api.examens.getReglagesExamens();
      if (!reponse?.success) throw new Error(reponse?.message || reponse?.error || 'Lecture impossible');
      setDonnees(reponse.data);
      setExamenFinal(reponse.data.examen_final);
      setBrouillon(Object.fromEntries(CODES.map((code) => [code, etatInitial(reponse.data.examens[code])])));
    } catch (erreur) {
      setEchec(erreur.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const modifies = useMemo(() => (donnees
    ? CODES.filter((code) => {
      const initial = etatInitial(donnees.examens[code]);
      const actuel = brouillon[code];
      return actuel && (actuel.mode !== initial.mode || !memesIds(actuel.ids, initial.ids));
    })
    : []), [donnees, brouillon]);
  const aDesModifications = modifies.length > 0;
  const desactive = !donnees?.disponible;

  // Régime A : le dernier examen part au serveur tout de suite ; un refus
  // ramène l'ancien choix à l'écran.
  const choisirExamenFinal = async (code) => {
    if (code === examenFinal || desactive) return;
    const avant = examenFinal;
    setExamenFinal(code);
    setEnregistrementFinal(true);
    try {
      const reponse = await api.examens.enregistrerExamenFinal(code);
      if (!reponse?.success) throw new Error(reponse?.message || reponse?.error || "L'enregistrement a échoué");
      notifier(reponse.message || 'Configuration mise à jour');
    } catch (erreur) {
      setExamenFinal(avant);
      notifier(erreur.message || "L'enregistrement a échoué", 'error');
    } finally {
      setEnregistrementFinal(false);
    }
  };

  const basculerClasse = (code, id) => setBrouillon((precedent) => {
    const ids = new Set(precedent[code].ids);
    if (ids.has(id)) ids.delete(id);
    else ids.add(id);
    return { ...precedent, [code]: { mode: 'reglage', ids } };
  });

  const revenirALaRegle = (code) => setBrouillon((precedent) => ({
    ...precedent,
    [code]: {
      mode: 'regle',
      ids: new Set(donnees.examens[code].classes.filter((c) => c.par_regle).map((c) => c.id))
    }
  }));

  const reprendreAnneePrecedente = async (code) => {
    setReprise(code);
    try {
      const reponse = await api.examens.getClassesAnneePrecedente(code);
      if (!reponse?.success) throw new Error(reponse?.message || reponse?.error || 'Lecture impossible');
      const data = reponse.data;
      if (!data?.annee) {
        notifier("Il n'y a pas d'année précédente.", 'info');
      } else if (!data.reglee) {
        notifier(`Rien n'était réglé pour le ${code} en ${data.annee.nom} : la règle du nom s'appliquait.`, 'info');
      } else {
        setBrouillon((precedent) => ({ ...precedent, [code]: { mode: 'reglage', ids: new Set(data.retrouvees) } }));
        setToutesVisibles((precedent) => ({ ...precedent, [code]: true }));
        notifier(
          `${data.retrouvees.length} classe${data.retrouvees.length > 1 ? 's' : ''} reprise${data.retrouvees.length > 1 ? 's' : ''} de ${data.annee.nom}`
            + (data.disparues.length > 0 ? `. Introuvable${data.disparues.length > 1 ? 's' : ''} cette année : ${data.disparues.join(', ')}.` : '.')
            + ' Vérifiez, puis enregistrez.',
          data.disparues.length > 0 ? 'info' : 'success'
        );
      }
    } catch (erreur) {
      notifier(erreur.message || 'Lecture impossible', 'error');
    } finally {
      setReprise(null);
    }
  };

  const reinitialiser = () => {
    setBrouillon(Object.fromEntries(CODES.map((code) => [code, etatInitial(donnees.examens[code])])));
  };

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      for (const code of modifies) {
        const { mode, ids } = brouillon[code];
        const reponse = await api.examens.enregistrerClassesExamen(code, mode === 'regle' ? null : [...ids]);
        if (!reponse?.success) throw new Error(`${code} : ${reponse?.message || reponse?.error || "l'enregistrement a échoué"}`);
      }
      notifier('Configuration mise à jour');
      await charger();
    } catch (erreur) {
      notifier(erreur.message || "L'enregistrement a échoué", 'error');
    } finally {
      setEnregistrement(false);
    }
  };

  if (chargement && !donnees) return <ChargementSection accent={ACCENT} libelle="Chargement des réglages…" />;

  if (echec && !donnees) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-red-600" />
            <div>
              <p className="text-sm font-semibold text-red-900">Les réglages des examens n&apos;ont pas pu être lus</p>
              <p className="mt-1 text-xs text-red-700">{echec}</p>
            </div>
          </div>
          <Button variant="outline" onClick={charger} className="h-9 border-red-200 text-red-700 hover:bg-red-100">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  const nomAnnee = donnees.annee?.nom || "l'année affichée";

  return (
    <div className="space-y-5">
      {/* En-tête de section */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl shadow-sm"
            style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_SOMBRE})` }}
          >
            <Users className="text-white" size={18} />
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-gray-900">Candidats et cycle</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Qui se présente à chaque examen en {nomAnnee}, et quel examen termine le cycle de l&apos;école
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reinitialiser}
            disabled={!aDesModifications || enregistrement}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <RotateCcw size={13} /> Réinitialiser
          </button>
          <button
            type="button"
            onClick={enregistrer}
            disabled={enregistrement || !aDesModifications || desactive}
            className={`flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${aDesModifications && !desactive ? 'hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'}`}
            style={aDesModifications && !desactive ? { backgroundColor: ACCENT } : undefined}
          >
            {enregistrement
              ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              : <Save size={13} />}
            Enregistrer
          </button>
        </div>
      </div>

      {desactive && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">
            Ces réglages ne sont pas encore installés dans cette école : KLASYS reconnaît les candidats par le nom de
            leur classe, et le dernier examen est le BAC. Ce qui s&apos;affiche ci-dessous est la règle appliquée aujourd&apos;hui.
          </p>
        </div>
      )}

      {/* L'encadré d'information de la section (un seul, §8). */}
      <div className="rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
        <div className="flex gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
            <Info className="h-4 w-4 text-[#246990]" />
          </div>
          <div className="text-xs leading-relaxed text-gray-600">
            <p className="mb-1 text-[13px] font-semibold text-[#246990]">Pourquoi régler les classes ?</p>
            <p>
              Sans réglage, KLASYS reconnaît les candidats par le nom de leur classe : CM2 pour le CEPE, 3e (sauf P3)
              pour le BEPC, Tle pour le BAC. Une classe nommée autrement disparaissait de la liste de l&apos;examen,
              des résultats, de l&apos;import et du devenir. Les classes cochées ici valent pour {nomAnnee} ; l&apos;an prochain,
              « Reprendre l&apos;année précédente » les retrouve par leur nom.
            </p>
          </div>
        </div>
      </div>

      {/* Dernier examen : régime A, s'enregistre au clic. */}
      <CarteReglages titre="Dernier examen de l'école" accent={ACCENT} compteur="S'enregistre aussitôt" dense>
        {CODES.map((code) => {
          const actif = examenFinal === code;
          return (
            <label
              key={code}
              className={`flex cursor-pointer items-center justify-between px-5 py-4 transition-all ${actif ? 'bg-white' : 'bg-gray-50/40'} hover:bg-gray-50/70 ${desactive ? 'cursor-not-allowed' : ''}`}
            >
              <div className="mr-4 flex flex-1 items-center gap-3">
                <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: actif ? ACCENT : '#9ca3af' }} />
                <div>
                  <p className={`text-sm font-semibold ${actif ? 'text-gray-800' : 'text-gray-400'}`}>
                    {EXAMENS_NATIONAUX[code]?.libelleLong || code}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">
                    {DESCRIPTIONS_FINAL[code]} Ses admis sortent du taux de réinscription réel.
                  </p>
                </div>
              </div>
              <input
                type="radio"
                name="examen_final"
                checked={actif}
                disabled={desactive || enregistrementFinal}
                onChange={() => choisirExamenFinal(code)}
                className={`h-4 w-4 flex-shrink-0 accent-[#319190] ${enregistrementFinal ? 'opacity-40' : ''}`}
                aria-label={`Dernier examen : ${code}`}
              />
            </label>
          );
        })}
      </CarteReglages>

      {/* Classes de chaque examen : régime B. */}
      {CODES.map((code) => {
        const examen = donnees.examens[code];
        const etat = brouillon[code] || etatInitial(examen);
        const retenues = examen.classes.filter((c) => etat.ids.has(c.id));
        const visibles = toutesVisibles[code]
          ? examen.classes
          : examen.classes.filter((c) => etat.ids.has(c.id) || c.par_regle);
        const cachees = examen.classes.length - visibles.length;
        const eleves = retenues.reduce((n, c) => n + c.eleves, 0);
        const modifie = modifies.includes(code);

        return (
          <CarteReglages
            key={code}
            titre={`${EXAMENS_NATIONAUX[code]?.libelleLong || code} - classes présentées`}
            accent={ACCENT}
            compteur={`${retenues.length} classe${retenues.length > 1 ? 's' : ''}, ${eleves} élève${eleves > 1 ? 's' : ''}`}
            dense
          >
            {visibles.length === 0 && (
              <p className="px-5 py-4 text-sm text-gray-500">
                Aucune classe ne porte un nom de {EXAMENS_NATIONAUX[code]?.libelleClasses} cette année.
              </p>
            )}
            {visibles.map((c) => {
              const actif = etat.ids.has(c.id);
              return (
                <div key={c.id} className={`flex items-center justify-between px-5 py-3 transition-all ${actif ? 'bg-white' : 'bg-gray-50/40'} hover:bg-gray-50/70`}>
                  <div className="mr-4 flex flex-1 items-center gap-3">
                    <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: actif ? ACCENT : '#9ca3af' }} />
                    <div>
                      <p className={`text-sm font-semibold ${actif ? 'text-gray-800' : 'text-gray-400'}`}>{c.nom}</p>
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        {c.eleves} élève{c.eleves > 1 ? 's' : ''}
                        {c.par_regle ? ' - reconnue par son nom' : ''}
                      </p>
                    </div>
                  </div>
                  <Interrupteur
                    actif={actif}
                    onBasculer={() => basculerClasse(code, c.id)}
                    desactive={desactive || enregistrement}
                    libelle={`${code} : ${c.nom}`}
                  />
                </div>
              );
            })}
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
              <p className="text-[11px] text-gray-500">
                {etat.mode === 'regle'
                  ? `Règle du nom des classes : rien n'est réglé pour ${nomAnnee}.`
                  : `Réglé à la main pour ${nomAnnee}.`}
                {modifie && <span className="font-semibold text-amber-700"> Non enregistré.</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                {cachees > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setToutesVisibles((p) => ({ ...p, [code]: true }))}>
                    Afficher les {cachees} autres classes
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => reprendreAnneePrecedente(code)} disabled={desactive || reprise === code}>
                  Reprendre l&apos;année précédente
                </Button>
                {etat.mode === 'reglage' && (
                  <Button variant="outline" size="sm" onClick={() => revenirALaRegle(code)} disabled={desactive}>
                    Revenir à la règle du nom
                  </Button>
                )}
              </div>
            </div>
          </CarteReglages>
        );
      })}

      {aDesModifications && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
            <AlertCircle size={15} className="text-amber-600" />
          </div>
          <p className="text-xs font-semibold text-amber-700">Vous avez des modifications non enregistrées.</p>
          <button
            type="button"
            onClick={enregistrer}
            disabled={enregistrement || desactive}
            className="ml-auto rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm"
            style={{ backgroundColor: ACCENT }}
          >
            {enregistrement ? 'Enregistrement…' : 'Enregistrer maintenant'}
          </button>
        </div>
      )}

      <PorteurNotification notification={notification} onFermer={masquer} />
    </div>
  );
};

export default ParametrageCandidatsExamens;
